import React, { useContext, useState } from 'react';

import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { PaymentSlipFormDialog } from '@components/admin/booking/PaymentSlipFormDialog';
import { PaymentSlipTable } from '@components/admin/booking/PaymentSlipTable';
import { Button } from '@components/Button';
import { QueryContainer } from '@components/QueryContainer';
import { VerticalSpace } from '@components/VerticalSpace';
import { paymentSlipStrings } from '@data/paymentSlip';
import { useDownloadPaymentSlipPdfs } from '@hooks/mutation/dashboard/useDownloadPaymentSlipPdfs';
import { usePatchBookingInquiry } from '@hooks/mutation/dashboard/usePatchBookingInquiry';
import { useTrackingData } from '@hooks/queries/dashboard/useTrackingData';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { PaymentSlipHelper } from '@models/next/PaymentSlip';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { foldMessagesEither } from '@utils/errors';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { useChecklist } from '@utils/useChecklist';

const strings = createStrings({
  ja: {
    createPaymentSlip: `${paymentSlipStrings.payment_slip}を作成する`,
    exportPaymentSlip: `${paymentSlipStrings.payment_slip}をエクスポート`,
    finishPaymentSlips: `${paymentSlipStrings.payment_slip}全部発行完了`,
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  paymentSlipOpBox: {
    ...common.flexInline,
    ...common.horizontalSpace1,
  },
}));

export const PaymentSlipPanel: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { bookingInquiry, preAlert, user } = useContext(BookingDetailsContext);
  const trackingData = useTrackingData(bookingInquiry.d2d_no);

  const paymentSlipsQuery = dashboardPaymentSlipApis.useGet('/dashboard/payment-slip', {
    queries: {
      d2d_no: bookingInquiry.d2d_no,
    },
  });

  const checklist = useChecklist([], paymentSlipsQuery.data?.results.map((d) => d.id) ?? []);

  const downloadPaymentSlipPdfs = useDownloadPaymentSlipPdfs(
    bookingInquiry.d2d_no,
    paymentSlipsQuery.data?.results.filter((item) => checklist.checked(item.id)) ?? [],
  );

  const canEdit = PaymentSlipHelper.canCreatePaymentSlip(user, preAlert);

  const [paymentSlipFormOpen, setPaymentSlipFormOpen] = useState(false);

  const dialogKey = useDialogKey(paymentSlipFormOpen);

  const confirmDialog = useConfirmDialog();

  const patchBookingInquiry = usePatchBookingInquiry(bookingInquiry.d2d_no);

  const canFinishPaymentSlips =
    trackingData.data && 'data' in trackingData.data && paymentSlipsQuery.data
      ? PaymentSlipHelper.canFinishPaymentSlips(
          user,
          bookingInquiry,
          trackingData.data,
          paymentSlipsQuery.data.results,
        )
      : undefined;

  return (
    <>
      <QueryContainer queries={[paymentSlipsQuery]}>
        {paymentSlipsQuery.data && paymentSlipsQuery.data.results.length > 0 && (
          <PaymentSlipTable items={paymentSlipsQuery.data.results} checklist={checklist} />
        )}
      </QueryContainer>

      <PaymentSlipFormDialog
        open={paymentSlipFormOpen}
        key={dialogKey}
        onClose={() => setPaymentSlipFormOpen(false)}
        preAlert={preAlert}
        bookingInquiry={bookingInquiry}
        type="create"
      />
      {confirmDialog.renderDialog()}

      <VerticalSpace height={16} />

      <div className={classes.paymentSlipOpBox}>
        <Button
          size="small"
          shape="round"
          text={strings.createPaymentSlip}
          type="button"
          color={foldMessagesEither(
            canEdit,
            () => 'grey',
            () => 'primary',
          )}
          onClick={() =>
            foldMessagesEither(
              canEdit,
              ({ messages }) => {
                confirmDialog.confirm({ message: messages });
              },
              () => setPaymentSlipFormOpen(true),
            )
          }
          aria-disabled={canEdit._tag === 'Left'}
        />

        <Button
          size="small"
          shape="round"
          text={strings.exportPaymentSlip}
          color="primary"
          disabled={checklist.checklist.length === 0}
          type="button"
          onClick={() => downloadPaymentSlipPdfs.mutateAsync()}
        />

        <Button
          size="small"
          shape="round"
          text={strings.finishPaymentSlips}
          color={
            canFinishPaymentSlips
              ? foldMessagesEither(
                  canFinishPaymentSlips,
                  () => 'grey',
                  () => 'primary',
                )
              : 'grey'
          }
          type="button"
          onClick={async () => {
            if (canFinishPaymentSlips?._tag !== 'Right') {
              await confirmDialog.confirm({
                title: formStrings.error,
                message: canFinishPaymentSlips?.left.messages.join('\n'),
              });

              return;
            }

            if (
              (await confirmDialog.confirm({
                message: paymentSlipStrings.warningPaymentSlipDone(bookingInquiry.is_invoice_done),
              })) === 'confirmed'
            ) {
              await patchBookingInquiry.mutateAsync({ is_payment_slip_done: true });
            }
          }}
        />
      </div>
    </>
  );
};
